/**
 * BetTableGoals – reference admin markup for the Goal game (rendered directly inside
 * .casino-table.five-cricket.super-over.detail-page-container.ball-by-ball.goal):
 *   .market-container > .market-6 x2 (Market6: "Who Will Goal Next?" = subtype player,
 *   "Method Of Next Goal" = subtype goal), each a .bet-table with a collapsible body.
 * goal2 (.casino-table….goal.goal2): player / method (.market-6, Back + Lay) and
 *   "Method Of Combination Goal" = subtype combo (.market-comb, two .col-md-6 columns, Back only).
 * Styled by the ported reference rules in styles/index.css (+ styles/games/goal.css).
 */
import { useState } from "react";

// Reference v-kval: size >= 1000 → one-decimal K
const kval = (size) => {
  const n = Number(size) || 0;
  return n >= 1000 ? `${parseFloat((n / 1000).toFixed(1))}K` : String(parseFloat(n));
};

// Reference v-kvalpoint: min/max >= 1000 → two-decimal K
const kvalpoint = (size) => {
  const n = Number(size) || 0;
  return n >= 1000 ? `${parseFloat((n / 1000).toFixed(2))}K` : String(parseFloat(n));
};

// Reference toCurrency (en-IN, up to 2 decimals); 0 is shown as "0"
const toCurrency = (v) =>
  v ? Number(v).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : 0;

const bySr = (a, b) => (Number(a?.sr) || 0) - (Number(b?.sr) || 0);

const BetTableGoals = ({ data = [], onBetClick, exposures = {}, goal2 = false }) => {
  const [open, setOpen] = useState({ player: true, goal: true, method: true, combo: true });

  const visible = (item) => item?.visible === undefined || Number(item?.visible) === 1;
  const bySubtype = (subtype) => data.filter((i) => i?.subtype === subtype && visible(i)).sort(bySr);
  const player = bySubtype("player");
  const goal = bySubtype("goal");
  const method = bySubtype("method");
  const combo = bySubtype("combo");

  // Booked markets come keyed "<game>:<market>|<runner>" (the shared "No Goal"
  // runner); everything else by the runner name.
  const getExposure = (name, subtype) => {
    if (!name) return 0;
    const market = subtype === "player" ? "player" : (subtype === "goal" || subtype === "method") ? "method" : null;
    if (market) {
      const booked = exposures[`${goal2 ? "goal2" : "goal"}:${market}|${name.trim()}`];
      if (booked !== undefined) return booked;
    }
    const keys = [name, name.trim(), name.toLowerCase(), name.toLowerCase().trim()];
    for (const key of keys) {
      if (key && exposures[key] !== undefined) return exposures[key];
    }
    return 0;
  };

  // goal2 (Goal 2): Back + Lay columns and a .bet-table-mobile-row per runner; the combination
  // market is .market-comb with two .col-md-6 columns (Back only).
  const headerCol = (colCls, withLay) => (
    <div className={colCls}>
      <div className="fancy-tripple">
        <div className="bet-table-row">
          <div className="nation-name" /> <div className="back bl-title back-title">Back</div>
          {withLay ? (
            <>
              {" "}
              <div className="lay bl-title lay-title">Lay</div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );

  const renderMarket = (index, title, items, { marketCls = "market-6", colCls = "col-6 col-md-12", withLay = false, mobileRow = false, headerCols = 1 } = {}) => (
    <div className={marketCls}>
      <div className="bet-table">
        <div
          data-toggle="collapse"
          data-target={`#market-${index}`}
          aria-expanded={open[index] ? "true" : "false"}
          className={`bet-table-header${open[index] ? "" : " collapsed"}`}
          onClick={() => setOpen((o) => ({ ...o, [index]: !o[index] }))}
        >
          <div className="nation-name">
            <span title={title}>{title}</span>
          </div>
        </div>{" "}
        <div
          id={`market-${index}`}
          className={`bet-table-body collapse${open[index] ? " show" : ""} container-fluid container-fluid-5`}
        >
          <div className="row row5 d-none-mobile">
            {headerCol(colCls, withLay)}
            {headerCols > 1 ? (
              <>
                {" "}
                {headerCol(colCls, withLay)}
              </>
            ) : null}
          </div>{" "}
          <div className="row row5">
            {items.map((item, idx) => {
              const odds = Number(item?.b) || 0;
              const size = Number(item?.bs) || 0;
              const suspended = item?.gstatus !== "OPEN";
              const book = Number(getExposure(item?.nat, item?.subtype)) || 0;
              const bookCls = book > 0 ? "book-green" : "book-red";
              const layOdds = Number(item?.l) || 0;
              const laySize = Number(item?.ls) || 0;
              return (
                <div className={colCls} key={item?.sid ?? idx}>
                  <div className="fancy-tripple">
                    {mobileRow ? (
                      <>
                        <div className="bet-table-mobile-row d-none-desktop">
                          <div className="bet-table-mobile-team-name">
                            <span>{item?.nat}</span> <span className={bookCls}>{toCurrency(book)}</span>
                          </div>
                        </div>{" "}
                      </>
                    ) : null}
                    <div className="bet-table-row">
                      <div className="nation-name d-none-mobile">
                        <p>{item?.nat}</p> <p className={`mb-0 ${bookCls}`}>{toCurrency(book)}</p>
                      </div>{" "}
                      <div
                        data-title={item?.gstatus}
                        className={`bl-box back${odds === 0 ? " no-val" : ""}${suspended ? " suspended" : ""}`}
                        onClick={() => !suspended && odds !== 0 && onBetClick?.(odds, item?.nat, item)}
                      >
                        <span className="d-block odds">{odds !== 0 ? item?.b : "—"}</span>{" "}
                        {size !== 0 && odds !== 0 ? <span className="d-block">{kval(size)}</span> : null}
                      </div>{" "}
                      {withLay ? (
                        <>
                          <div
                            className={`bl-box lay${layOdds === 0 ? " no-val" : ""}${suspended ? " suspended" : ""}`}
                            onClick={() => !suspended && layOdds !== 0 && onBetClick?.(layOdds, item?.nat, item)}
                          >
                            <span className="d-block odds">{layOdds !== 0 ? item?.l : "—"}</span>{" "}
                            {laySize !== 0 && layOdds !== 0 ? <span className="d-block">{kval(laySize)}</span> : null}
                          </div>{" "}
                        </>
                      ) : null}
                      <div className="fancy-min-max">
                        Min:<span>{kvalpoint(item?.min)}</span> Max:<span>{kvalpoint(item?.max)}</span>
                      </div>
                    </div>{" "}
                    {item?.rem ? <div className="market-message">{item.rem}</div> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );

  if (goal2) {
    const g2 = { withLay: true, mobileRow: true };
    return (
      <div className="market-container">
        {player.length ? renderMarket("player", "Who Will Goal Next?", player, g2) : null}{" "}
        {method.length ? renderMarket("method", "Method Of Next Goal", method, g2) : null}{" "}
        {combo.length
          ? renderMarket("combo", "Method Of Combination Goal", combo, {
              marketCls: "market-comb",
              colCls: "col-6 col-md-6",
              mobileRow: true,
              headerCols: 2,
            })
          : null}
      </div>
    );
  }

  return (
    <div className="market-container">
      {player.length ? renderMarket("player", "Who Will Goal Next?", player) : null}{" "}
      {goal.length ? renderMarket("goal", "Method Of Next Goal", goal) : null}
    </div>
  );
};

export default BetTableGoals;
